/**
 * The 16-color palette tags can be assigned. Computed at the same
 * saturation/lightness as the theme's existing accent color
 * (--accent: #33cc66 → HSL 140°, 60%, 50%), stepping the hue by 360/16 =
 * 22.5° so every color reads as "the same design language, different hue"
 * rather than a mismatched set. 'green' lands exactly on today's accent, so
 * a tag created before this palette existed needs no visual change.
 */

export interface TagColorDef {
  key: string;
  hex: string;
  /** Catalan label, shown as the swatch's tooltip in the color picker. */
  label: string;
}

export const TAG_COLORS: TagColorDef[] = [
  { key: 'green', hex: '#33cc66', label: 'Verd' },
  { key: 'teal', hex: '#33cc9f', label: 'Turquesa' },
  { key: 'cyan', hex: '#33bfcc', label: 'Cian' },
  { key: 'sky', hex: '#3386cc', label: 'Blau cel' },
  { key: 'blue', hex: '#334dcc', label: 'Blau' },
  { key: 'indigo', hex: '#5333cc', label: 'Indi' },
  { key: 'violet', hex: '#8c33cc', label: 'Violeta' },
  { key: 'purple', hex: '#c633cc', label: 'Porpra' },
  { key: 'magenta', hex: '#cc3399', label: 'Magenta' },
  { key: 'pink', hex: '#cc3360', label: 'Rosa' },
  { key: 'red', hex: '#cc4033', label: 'Vermell' },
  { key: 'orange', hex: '#cc7933', label: 'Taronja' },
  { key: 'amber', hex: '#ccb233', label: 'Ambre' },
  { key: 'yellow', hex: '#accc33', label: 'Groc' },
  { key: 'lime', hex: '#73cc33', label: 'Llima' },
  { key: 'spring', hex: '#39cc33', label: 'Verd primavera' },
];

const DEFAULT_COLOR_KEY = 'green';

/** The hex for a color key, falling back to green for unknown/missing keys
 *  (e.g. a tag whose color predates this palette, or bad stored data). */
export function getTagColorHex(colorKey: string | undefined | null): string {
  const found = TAG_COLORS.find((c) => c.key === colorKey);
  return (found ?? TAG_COLORS.find((c) => c.key === DEFAULT_COLOR_KEY)!).hex;
}

/** Inline background/border/text style for a tag chip of the given color —
 *  the same derivation the theme uses for --accent-alpha-13/27 off of
 *  --accent (hex + alpha suffix), just computed per-tag instead of fixed. */
export function tagChipStyle(colorKey: string | undefined | null): string {
  const hex = getTagColorHex(colorKey);
  return `background-color:${hex}22;border-color:${hex}44;color:${hex};`;
}
